import Decimal from 'decimal.js';

import {
  DispositiveProvisionDispositionKind,
  DispositiveProvisionTemplateKind,
  DispositiveProvisionTransferTaxKind,
  TestamentaryEntityKind,
} from '@/types/schema';

import {
  DispositiveProvisionsFormRecipient,
  DispositiveProvisionsTemplateSlotKind,
} from '../../DispositiveProvisionsForm/DispositiveProvisionsForm.types';
import {
  DispositiveProvisionsRecipientTemplateRecipientConfiguration,
  DispositiveProvisionsRecipientTypes,
} from '../../DispositiveProvisionsForm/DispositiveProvisionsFormRecipients/DispositiveProvisionsRecipient.types';
import { DraggableListItemRecipient } from '../../DispositiveProvisionsForm/DispositiveProvisionsFormRecipients/DraggableListItemRecipient';

const DISCLAIMER_TRUST_RECIPIENT_CONFIGURATION: DispositiveProvisionsRecipientTemplateRecipientConfiguration =
  {
    allowsIndividualRecipient: false,
    allowsOrganizationRecipient: false,
    allowsEntityRecipient: false,
    allowsTestamentaryEntityRecipient: true,
    createRecipientTypes: [
      DispositiveProvisionsRecipientTypes.TestamentaryEntity,
    ],
    testamentaryEntityTaxSettings: {
      estateInclusionStatus: 'out-of-estate',
      survivingSpouseStateInEstateStatus: 'out-of-estate',
    },
    defaultNewTestamentaryEntityName: 'Disclaimer Trust',
    defaultNewTestamentaryEntityKind: TestamentaryEntityKind.TestamentaryTrust,
  };

export const BLANK_BYPASS_TRUST: DispositiveProvisionsFormRecipient = {
  _templateSlotKind: DispositiveProvisionsTemplateSlotKind.AbBypass,
  recipientId: '', // blank string instead of null to avoid MUI warning
  recipientKind: null,
  dispositionAmount: null,
  dispositionPercentage: null,
  notes: '',
  _dispositiveProvisionId: null,
  assetGroupIds: [],
  assetStaticIds: [],
  insurancePolicyIds: [],
  ownershipStakeIds: [],
  dispositionAssetPercentage: null,
  transferTaxKind: DispositiveProvisionTransferTaxKind.SpouseCreditShelter,
  dispositionKind_ButtonGroup: 'other',
  dispositionKind_Select:
    DispositiveProvisionDispositionKind.RemainingLifetimeExclusionOfGrantor,
};

export interface TemplateRecipientSlotDescriptor {
  templateType: DispositiveProvisionTemplateKind;
  emptyRecipientOptionDisplay: string;
  recipientConfiguration?: Partial<DispositiveProvisionsRecipientTemplateRecipientConfiguration>;
  isOptional?: boolean;
}

const MARITAL_TRUST_RECIPIENT_CONFIGURATION: Partial<DispositiveProvisionsRecipientTemplateRecipientConfiguration> =
  {
    testamentaryEntityTaxSettings: {
      estateInclusionStatus: 'in-estate',
      survivingSpouseStateInEstateStatus: 'in-estate',
    },
    defaultNewTestamentaryEntityName: 'Marital trust',
    defaultNewTestamentaryEntityKind: TestamentaryEntityKind.TestamentaryTrust,
  };

export const TEMPLATE_RECIPIENT_SLOT_DESCRIPTORS: Record<
  DispositiveProvisionsTemplateSlotKind,
  TemplateRecipientSlotDescriptor
> = {
  [DispositiveProvisionsTemplateSlotKind.Generic]: {
    templateType: DispositiveProvisionTemplateKind.Default,
    emptyRecipientOptionDisplay: 'Select recipient',
  },
  [DispositiveProvisionsTemplateSlotKind.Pourover]: {
    templateType: DispositiveProvisionTemplateKind.Default,
    emptyRecipientOptionDisplay: 'Select trust',
  },
  [DispositiveProvisionsTemplateSlotKind.OutrightSurvivingSpouse]: {
    templateType: DispositiveProvisionTemplateKind.OutrightToSurvivingSpouse,
    emptyRecipientOptionDisplay: 'Select spouse',
  },
  [DispositiveProvisionsTemplateSlotKind.OutrightDisclaimer]: {
    templateType: DispositiveProvisionTemplateKind.OutrightToSurvivingSpouse,
    emptyRecipientOptionDisplay: 'Select disclaimer trust',
    recipientConfiguration: DISCLAIMER_TRUST_RECIPIENT_CONFIGURATION,
  },
  [DispositiveProvisionsTemplateSlotKind.MaritalTrust]: {
    templateType: DispositiveProvisionTemplateKind.MaritalTrust,
    emptyRecipientOptionDisplay: 'Select marital trust',
    recipientConfiguration: MARITAL_TRUST_RECIPIENT_CONFIGURATION,
  },
  [DispositiveProvisionsTemplateSlotKind.MaritalDisclaimer]: {
    templateType: DispositiveProvisionTemplateKind.MaritalTrust,
    emptyRecipientOptionDisplay: 'Select disclaimer trust',
    recipientConfiguration: DISCLAIMER_TRUST_RECIPIENT_CONFIGURATION,
  },
  [DispositiveProvisionsTemplateSlotKind.MaritalClaytonQtip]: {
    templateType: DispositiveProvisionTemplateKind.MaritalTrust,
    emptyRecipientOptionDisplay: 'Select QTIP marital trust',
    recipientConfiguration: {
      ...MARITAL_TRUST_RECIPIENT_CONFIGURATION,
      defaultNewTestamentaryEntityName: 'QTIP marital trust',
    },
  },
  [DispositiveProvisionsTemplateSlotKind.MaritalBypass]: {
    templateType: DispositiveProvisionTemplateKind.MaritalTrust,
    emptyRecipientOptionDisplay: 'Select bypass trust',
  },
  [DispositiveProvisionsTemplateSlotKind.AbSurvivor]: {
    templateType: DispositiveProvisionTemplateKind.AbTrust,
    emptyRecipientOptionDisplay: 'Select survivor’s trust',
    recipientConfiguration: {
      defaultNewTestamentaryEntityName: "Survivor's trust",
      defaultNewTestamentaryEntityKind:
        TestamentaryEntityKind.TestamentaryTrust,
      testamentaryEntityTaxSettings: {
        estateInclusionStatus: 'in-estate',
        survivingSpouseStateInEstateStatus: 'in-estate',
      },
    },
  },
  [DispositiveProvisionsTemplateSlotKind.AbBypass]: {
    templateType: DispositiveProvisionTemplateKind.AbTrust,
    emptyRecipientOptionDisplay: 'Select bypass trust',
    recipientConfiguration: {
      defaultNewTestamentaryEntityName: 'Bypass trust',
      defaultNewTestamentaryEntityKind:
        TestamentaryEntityKind.TestamentaryTrust,
      testamentaryEntityTaxSettings: {
        estateInclusionStatus: 'out-of-estate',
        survivingSpouseStateInEstateStatus: 'out-of-estate',
      },
    },
  },
  [DispositiveProvisionsTemplateSlotKind.AbGstExempt]: {
    templateType: DispositiveProvisionTemplateKind.AbTrust,
    emptyRecipientOptionDisplay: 'Select GST exempt marital trust',
    isOptional: true,
    recipientConfiguration: {
      defaultNewTestamentaryEntityName: 'Marital trust (GST exempt)',
      defaultNewTestamentaryEntityKind:
        TestamentaryEntityKind.TestamentaryTrust,
      testamentaryEntityTaxSettings: {
        estateInclusionStatus: 'in-estate',
        survivingSpouseStateInEstateStatus: 'in-estate',
        gstStatus: 'gst-exempt',
      },
    },
  },
  [DispositiveProvisionsTemplateSlotKind.AbGstNonExempt]: {
    templateType: DispositiveProvisionTemplateKind.AbTrust,
    emptyRecipientOptionDisplay: 'Select GST non-exempt marital trust',
    recipientConfiguration: {
      defaultNewTestamentaryEntityName: 'Marital trust (GST non-exempt)',
      defaultNewTestamentaryEntityKind:
        TestamentaryEntityKind.TestamentaryTrust,
      testamentaryEntityTaxSettings: {
        estateInclusionStatus: 'in-estate',
        survivingSpouseStateInEstateStatus: 'in-estate',
        gstStatus: 'gst-non-exempt',
      },
    },
  },
  [DispositiveProvisionsTemplateSlotKind.AbcCreditShelter]: {
    templateType: DispositiveProvisionTemplateKind.AbcTrust,
    emptyRecipientOptionDisplay:
      'Select credit shelter (federal & state exempt)',
    recipientConfiguration: {
      defaultNewTestamentaryEntityName: 'Credit shelter trust',
      defaultNewTestamentaryEntityKind:
        TestamentaryEntityKind.TestamentaryTrust,
      testamentaryEntityTaxSettings: {
        estateInclusionStatus: 'out-of-estate',
        survivingSpouseStateInEstateStatus: 'out-of-estate',
        gstStatus: 'gst-non-exempt',
      },
    },
  },
  [DispositiveProvisionsTemplateSlotKind.AbcFederalExemptStateNonExempt]: {
    templateType: DispositiveProvisionTemplateKind.AbcTrust,
    emptyRecipientOptionDisplay:
      'Select marital trust (federal exempt, state non-exempt)',
    recipientConfiguration: {
      defaultNewTestamentaryEntityName: 'State QTIP marital trust',
      defaultNewTestamentaryEntityKind:
        TestamentaryEntityKind.TestamentaryTrust,
      includeSpouseFederalCreditShelterStateMaritalExclusion: true,
      testamentaryEntityTaxSettings: {
        estateInclusionStatus: 'out-of-estate',
        survivingSpouseStateInEstateStatus: 'in-estate',
        gstStatus: 'gst-non-exempt',
      },
    },
  },
  [DispositiveProvisionsTemplateSlotKind.AbcGstExempt]: {
    templateType: DispositiveProvisionTemplateKind.AbcTrust,
    emptyRecipientOptionDisplay: 'Select GST exempt marital trust',
    isOptional: true,
    recipientConfiguration: {
      defaultNewTestamentaryEntityName: 'Marital trust (GST exempt)',
      defaultNewTestamentaryEntityKind:
        TestamentaryEntityKind.TestamentaryTrust,
      testamentaryEntityTaxSettings: {
        estateInclusionStatus: 'in-estate',
        survivingSpouseStateInEstateStatus: 'in-estate',
        gstStatus: 'gst-exempt',
      },
    },
  },
  [DispositiveProvisionsTemplateSlotKind.AbcGstNonExempt]: {
    templateType: DispositiveProvisionTemplateKind.AbcTrust,
    emptyRecipientOptionDisplay: 'Select GST non-exempt marital trust',
    isOptional: true,
    recipientConfiguration: {
      defaultNewTestamentaryEntityName: 'Marital trust (GST non-exempt)',
      defaultNewTestamentaryEntityKind:
        TestamentaryEntityKind.TestamentaryTrust,
      testamentaryEntityTaxSettings: {
        estateInclusionStatus: 'in-estate',
        survivingSpouseStateInEstateStatus: 'in-estate',
        gstStatus: 'gst-non-exempt',
      },
    },
  },
};

export function TemplateRecipientSlot({
  index,
  onRemove,
  slotKind,
}: {
  index: number;
  onRemove: () => void;
  slotKind?: DispositiveProvisionsTemplateSlotKind;
}) {
  const descriptor =
    TEMPLATE_RECIPIENT_SLOT_DESCRIPTORS[
      slotKind ?? DispositiveProvisionsTemplateSlotKind.Generic
    ];

  return (
    <DraggableListItemRecipient
      index={index}
      Draghandle={null}
      onRemove={onRemove}
      defaultExpanded
      templateType={descriptor.templateType}
      emptyRecipientOptionDisplay={descriptor.emptyRecipientOptionDisplay}
      recipientConfiguration={descriptor.recipientConfiguration}
      isOptional={descriptor.isOptional}
    />
  );
}

export const BLANK_MARITAL_TRUST: DispositiveProvisionsFormRecipient = {
  _templateSlotKind: DispositiveProvisionsTemplateSlotKind.MaritalTrust,
  recipientId: '', // blank string instead of null to avoid MUI warning
  recipientKind: null,
  dispositionKind_ButtonGroup: DispositiveProvisionDispositionKind.Percentage,
  dispositionKind_Select: '',
  dispositionAmount: null,
  dispositionPercentage: new Decimal(100),
  notes: '',
  transferTaxKind: DispositiveProvisionTransferTaxKind.SpouseMaritalExclusion,
  _dispositiveProvisionId: null,
  assetGroupIds: [],
  assetStaticIds: [],
  insurancePolicyIds: [],
  ownershipStakeIds: [],
  dispositionAssetPercentage: null,
};
